import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { LegalPage } from '@/constants/legal'

import { motion } from 'motion/react'
import { enter } from '@/lib/motion'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { SplitText } from '@/components/motion/split-text'
import { legalPages } from '@/constants/legal'

interface LegalPageProps {
    page: LegalPage
}

const LegalDocument: FC<LegalPageProps> = ({ page }): ReactNode => {
    return (
        <Layout>
            <Seo
                title={page.title}
                description={`${page.title} for the website of Zaki Hatta.`}
                noIndex
            />

            <section
                id='hero'
                className='relative z-[2] flex h-[40vh] w-full flex-col items-end justify-end overflow-clip bg-background px-4 pb-6 pt-hero lg:h-[56vh] lg:px-6'
            >
                <div className='flex w-full flex-col items-start overflow-clip md:max-w-[68%]'>
                    <SplitText
                        text="The"
                        effect='fade'
                        trigger='mount'
                        delay={0.2}
                        accessible={false}
                        className='t-display-serif block whitespace-pre text-foreground'
                    />

                    <SplitText
                        as='h1'
                        text={page.title}
                        effect='slide'
                        trigger='mount'
                        delay={0.35}
                        className='t-display block max-w-[600px] text-left text-foreground'
                    />
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={enter(0.3)}
                    className='flex w-full items-center gap-2 pt-6 md:max-w-[68%]'
                >
                    <p className='t-caps whitespace-nowrap text-foreground'>
                        Last updated:
                    </p>

                    <p className='t-meta flex-1 text-left text-foreground'>
                        <time dateTime={page.updated}>
                            {page.updatedLabel}
                        </time>
                    </p>
                </motion.div>
            </section>

            <section
                id='policy'
                className='relative z-[2] flex w-full flex-col items-center bg-background px-4 pb-section-lg pt-10 lg:px-6'
            >
                <div className='flex w-full flex-col items-start md:items-end'>
                    <div className='rich-text w-full max-w-[90%] md:max-w-[68%]'>
                        <h4>
                            {page.intro}
                        </h4>
                    </div>

                    <div
                        className='rich-text mt-12 w-full max-w-[90%] md:max-w-[68%]'
                        dangerouslySetInnerHTML={{ __html: page.body }}
                    />
                </div>
            </section>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: legalPages.map((page) => ({ params: { slug: page.slug } })),
        fallback: false
    }
}

export const getStaticProps: GetStaticProps<LegalPageProps, { slug: string }> = async ({ params }) => {
    const page = legalPages.find((item) => item.slug === params?.slug)

    if (!page) return { notFound: true }

    return { props: { page } }
}

export default LegalDocument
